import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Activity, CheckSquare, FolderOpen, Ghost, Globe2, Plus, RefreshCw, Search,
  Server, ShieldCheck, Sparkles, Square, Trash2, Users, X,
} from "lucide-react";
import { toast } from "sonner";
import { AdminCustomDomainsPanel } from "./AdminCustomDomainsPanel";
import { DomainDetailPanel } from "./DomainDetailPanel";
import { DomainGroupManager } from "./DomainGroupManager";
import { EmptyState } from "../../common/StateContainer";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";

export const DomainPoolWorkspace = ({ api, headers, domains, groups, users, customDomains, customBusyId, form, groupForm, controls, actions }) => {
  const [showAdd, setShowAdd] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [ghostCount, setGhostCount] = useState(null);
  const [ghostBusy, setGhostBusy] = useState(false);

  const loadGhosts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${api}/admin/domains/orphan-ghosts`, { headers });
      setGhostCount(data.count);
    } catch (_e) {
      setGhostCount(null);
    }
  }, [api, headers]);

  useEffect(() => { loadGhosts(); }, [loadGhosts]);

  const sweepGhosts = useCallback(async () => {
    if (!ghostCount) return;
    if (!window.confirm(`Flip is_custom:false on ${ghostCount} orphan ghost domain(s)? They'll appear in every licensed user's Available Pool immediately.`)) return;
    setGhostBusy(true);
    try {
      const { data } = await axios.post(`${api}/admin/domains/orphan-ghosts/sweep`, {}, { headers });
      toast.success(`Swept ${data.swept} ghost domain(s) back into the shared pool`);
      await loadGhosts();
      actions.refresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ghost sweep failed");
    } finally {
      setGhostBusy(false);
    }
  }, [api, headers, ghostCount, loadGhosts, actions]);

  const filtered = useMemo(() => domains.filter((domain) => {
    const query = controls.search.toLowerCase();
    const queryMatch = !query || domain.domain.toLowerCase().includes(query) || domain.notes?.toLowerCase().includes(query);
    const filterMatch =
      controls.filter === "all" ||
      (controls.filter === "ungrouped" && !domain.group_id) ||
      (controls.filter === "unassigned" && !domain.assigned_users?.length) ||
      (controls.filter === "old" && domain.tags?.includes("old")) ||
      (controls.filter === "flagged" && domain.tags?.includes("flagged")) ||
      domain.group_id === controls.filter;
    return queryMatch && filterMatch;
  }), [controls.filter, controls.search, domains]);

  useEffect(() => { if (!selectedId || !domains.some((domain) => domain.id === selectedId)) setSelectedId(filtered[0]?.id || domains[0]?.id || null); }, [domains, filtered, selectedId]);

  const selected = domains.find((domain) => domain.id === selectedId) || null;
  const allVisibleSelected = filtered.length > 0 && filtered.every((domain) => controls.selectedIds.includes(domain.id));

  const metrics = [
    { label: "Total",      value: domains.length,                                                         icon: Globe2,        accent: { bg: "bg-slate-800/60",   icon: "text-slate-300",   value: "text-slate-100" } },
    { label: "Assigned",   value: domains.filter((d) => d.assigned_users?.length).length,                 icon: Users,         accent: { bg: "bg-emerald-500/10", icon: "text-emerald-300", value: "text-emerald-300" } },
    { label: "Unassigned", value: domains.filter((d) => !d.assigned_users?.length).length,                icon: Activity,      accent: { bg: "bg-amber-500/10",   icon: "text-amber-300",   value: "text-amber-300" } },
    { label: "On servers", value: domains.filter((d) => d.server_id).length,                              icon: Server,        accent: { bg: "bg-cyan-500/10",    icon: "text-cyan-300",    value: "text-cyan-300" } },
    { label: "Flagged",    value: domains.filter((d) => d.tags?.includes("flagged")).length,              icon: ShieldCheck,   accent: { bg: "bg-rose-500/10",    icon: "text-rose-300",    value: "text-rose-300" } },
  ];

  const addGroupOptions = groups.map((g) => <SelectItem key={`add-${g.id}`} value={g.id}>{g.name}</SelectItem>);
  const filterGroupOptions = groups.map((g) => <SelectItem key={`filter-${g.id}`} value={g.id}>{g.name}</SelectItem>);

  return (
    <div className="mx-auto w-full max-w-[1800px] space-y-6 wx-fade-up" data-testid="domain-pool-workspace">
      {/* ===== Header ===== */}
      <header className="flex flex-col gap-5 border-b border-slate-800/80 pb-6 xl:flex-row xl:items-end xl:justify-between">
        <div className="space-y-3">
          <span className="wx-tactical"><Globe2 className="h-3 w-3" /> Managed inventory</span>
          <h1 className="font-orbitron text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl" data-testid="domain-pool-title">Domain Pool</h1>
          <p className="max-w-2xl text-sm text-slate-400">Classify, inspect, assign, scan, and permanently remove shared domains across the fleet.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {ghostCount > 0 && (
            <button
              type="button"
              onClick={sweepGhosts}
              disabled={ghostBusy}
              className="wx-btn border border-amber-500/40 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20"
              data-testid="sweep-ghosts-btn"
              title="Repatriate ghost custom-flagged pool entries that have no owner and no assignment"
            >
              {ghostBusy ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Ghost className="h-4 w-4" />}
              Sweep {ghostCount} ghost{ghostCount === 1 ? "" : "s"}
            </button>
          )}
          <button type="button" onClick={() => setShowCustom((v) => !v)} className="wx-btn-secondary" data-testid="toggle-custom-domains-button">
            <Users className="h-4 w-4" />
            User domains · <span className="font-mono tabular-nums">{customDomains.length}</span>
          </button>
          <button type="button" onClick={() => actions.setShowGroups(!controls.showGroups)} className="wx-btn-secondary" data-testid="toggle-domain-groups-button">
            <FolderOpen className="h-4 w-4" /> Groups
          </button>
          <button type="button" onClick={() => setShowAdd((v) => !v)} className="wx-btn-primary" data-testid="toggle-add-domain-button">
            <Plus className="h-4 w-4" /> Add domain
          </button>
        </div>
      </header>

      {/* ===== KPI strip ===== */}
      <section className="grid grid-cols-2 gap-3 md:grid-cols-5" data-testid="domain-pool-metrics">
        {metrics.map(({ label, value, icon: Icon, accent }) => (
          <div key={label} className="wx-card !p-4 flex flex-col gap-2.5">
            <span className={`grid h-9 w-9 place-items-center rounded-lg border border-slate-700/40 ${accent.bg}`}>
              <Icon className={`h-4 w-4 ${accent.icon}`} />
            </span>
            <div>
              <p className={`font-mono text-2xl font-bold tabular-nums ${accent.value}`} data-testid={`domain-metric-${label.toLowerCase().replace(/\s+/g, "-")}`}>{value}</p>
              <p className="wx-metric-label mt-0.5">{label}</p>
            </div>
          </div>
        ))}
      </section>

      {/* ===== Expansion panels ===== */}
      {showCustom && (
        <AdminCustomDomainsPanel domains={customDomains} busyId={customBusyId} onRefresh={actions.refreshCustom} onRetry={actions.retryCustom} onRevoke={actions.revokeCustom} />
      )}
      {controls.showGroups && <DomainGroupManager groups={groups} {...groupForm} />}

      {showAdd && (
        <section className="wx-card !p-0 overflow-hidden" data-testid="add-domain-panel">
          <header className="border-b border-slate-800/70 px-5 py-4">
            <p className="wx-overline text-cyan-300">Inventory intake</p>
            <h2 className="mt-1 text-base font-semibold text-slate-100">Add managed domain</h2>
          </header>
          <div className="grid gap-3 p-5 md:grid-cols-2 xl:grid-cols-3">
            <input value={form.domain} onChange={(e) => form.setDomain(e.target.value)} placeholder="example.com" className="input-dark" data-testid="new-domain-input" />
            <Select value={form.group || "__none__"} onValueChange={(v) => form.setGroup(v === "__none__" ? "" : v)}>
              <SelectTrigger className="input-dark" data-testid="new-domain-group-select"><SelectValue placeholder="No group" /></SelectTrigger>
              <SelectContent><SelectItem value="__none__">No group</SelectItem>{addGroupOptions}</SelectContent>
            </Select>
            <select value={form.rating} onChange={(e) => form.setRating(e.target.value)} className="input-dark" data-testid="new-domain-rating-select">
              <option value="excellent">Excellent inbox</option>
              <option value="good">Good inbox</option>
              <option value="average">Average</option>
              <option value="poor">Poor / burned</option>
            </select>
            <input value={form.tags} onChange={(e) => form.setTags(e.target.value)} placeholder="Tags, comma separated" className="input-dark" data-testid="new-domain-tags-input" />
            <input value={form.notes} onChange={(e) => form.setNotes(e.target.value)} placeholder="Operational notes" className="input-dark md:col-span-2" data-testid="new-domain-notes-input" />
          </div>
          <div className="flex justify-end border-t border-slate-800/70 px-5 py-4">
            <button type="button" onClick={actions.addDomain} disabled={!form.domain} className="wx-btn-primary" data-testid="add-domain-button">
              <Plus className="h-4 w-4" /> Add to pool
            </button>
          </div>
        </section>
      )}

      {/* ===== Toolbar ===== */}
      <section className="wx-card !p-0 overflow-hidden" data-testid="domain-pool-toolbar">
        <div className="grid gap-3 p-4 md:grid-cols-[minmax(220px,1fr)_240px_auto]">
          <label className="relative block">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <input
              value={controls.search}
              onChange={(e) => actions.setSearch(e.target.value)}
              placeholder="Search domains or notes"
              className="input-dark h-10 w-full pl-9"
              data-testid="domain-search-input"
            />
          </label>
          <Select value={controls.filter} onValueChange={actions.setFilter}>
            <SelectTrigger className="input-dark h-10 w-full" data-testid="domain-filter-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All managed domains</SelectItem>
              <SelectItem value="unassigned">Unassigned</SelectItem>
              <SelectItem value="ungrouped">Ungrouped</SelectItem>
              <SelectItem value="old">Previously used</SelectItem>
              <SelectItem value="flagged">Flagged</SelectItem>
              {filterGroupOptions}
            </SelectContent>
          </Select>
          <button
            type="button"
            onClick={() => actions.healthCheck(domains.map((d) => d.id))}
            disabled={controls.healthLoading || !domains.length}
            className="wx-btn-outline h-10"
            data-testid="check-all-health-btn"
          >
            {controls.healthLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            Scan all
          </button>
        </div>
        {controls.selectedIds.length > 0 && (
          <div className="flex flex-col gap-3 border-t border-cyan-500/15 bg-cyan-500/5 px-4 py-3 sm:flex-row sm:items-center sm:justify-between" data-testid="bulk-action-bar">
            <p className="text-sm text-slate-200">
              <span className="font-mono font-bold text-cyan-300">{controls.selectedIds.length}</span>
              <span className="ml-1 text-slate-400">selected</span>
            </p>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => actions.healthCheck(controls.selectedIds)} disabled={controls.healthLoading} className="wx-btn-secondary" data-testid="bulk-health-check-btn">
                <ShieldCheck className="h-4 w-4" /> Check health
              </button>
              <button type="button" onClick={actions.bulkDelete} disabled={controls.bulkDeleteLoading} className="wx-btn-danger" data-testid="bulk-delete-btn">
                <Trash2 className="h-4 w-4" /> Delete
              </button>
              <button type="button" onClick={() => actions.setSelectedIds([])} className="wx-btn-ghost" data-testid="clear-domain-selection-button">Clear</button>
            </div>
          </div>
        )}
      </section>

      {controls.healthResults && (
        <section className="rounded-xl border border-amber-500/30 bg-amber-500/5" data-testid="domain-health-results">
          <header className="flex items-center justify-between border-b border-amber-500/20 px-4 py-3">
            <p className="text-sm font-semibold text-amber-200">Health scan results</p>
            <button type="button" onClick={() => actions.setHealthResults(null)} className="text-slate-500 hover:text-slate-200" data-testid="close-domain-health-results"><X className="h-4 w-4" /></button>
          </header>
          <div className="max-h-56 divide-y divide-slate-800/70 overflow-y-auto">
            {controls.healthResults.map((r, i) => (
              <div key={`${r.domain}-${i}`} className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-center sm:justify-between" data-testid={`domain-health-result-${i}`}>
                <p className={`font-mono text-xs ${r.status === "ok" ? "text-emerald-300" : r.status === "flagged" ? "text-rose-300" : "text-amber-300"}`}>{r.domain}</p>
                <p className="text-xs text-slate-500">{r.issues?.join(" · ") || "No issues"}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ===== Main grid ===== */}
      <div className="grid min-w-0 gap-5 lg:grid-cols-12">
        <section className="wx-card !p-0 min-w-0 overflow-hidden lg:col-span-7 xl:col-span-8" data-testid="managed-domain-list">
          <header className="flex items-center justify-between border-b border-slate-800/70 px-4 py-3">
            <div>
              <p className="wx-overline">Visible inventory</p>
              <p className="mt-0.5 text-sm text-slate-200" data-testid="visible-domain-count">
                <span className="font-mono tabular-nums">{filtered.length}</span> domain{filtered.length === 1 ? "" : "s"}
              </p>
            </div>
            <button
              type="button"
              onClick={() => actions.toggleSelectAll(filtered)}
              className="flex items-center gap-2 text-xs text-slate-400 transition-colors hover:text-cyan-300"
              data-testid="select-all-domains"
            >
              {allVisibleSelected ? <CheckSquare className="h-4 w-4 text-cyan-400" /> : <Square className="h-4 w-4" />}
              {allVisibleSelected ? "Clear visible" : "Select visible"}
            </button>
          </header>
          <div className="max-h-[720px] overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="p-6" data-testid="domain-pool-empty">
                <EmptyState
                  icon={Globe2}
                  title="No domains match these filters"
                  description={controls.search ? "Try clearing the search box or switching to All managed domains." : "Add your first domain to the pool to begin."}
                  action={!controls.search ? { label: "Add domain", onClick: () => setShowAdd(true), icon: Plus } : null}
                  compact
                />
              </div>
            ) : (
              <div className="divide-y divide-slate-800/60">
                {filtered.map((domain, index) => {
                  const active = selected?.id === domain.id;
                  const checked = controls.selectedIds.includes(domain.id);
                  return (
                    <article
                      key={domain.id}
                      className={`grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 px-4 py-3 transition-colors ${active ? "bg-cyan-500/8" : "hover:bg-slate-900/60"}`}
                      data-testid={`managed-domain-row-${index}`}
                    >
                      <button
                        type="button"
                        onClick={() => actions.toggleSelection(domain.id)}
                        className={`grid h-5 w-5 place-items-center rounded border transition-colors ${checked ? "border-cyan-400 bg-cyan-400 text-slate-950" : "border-slate-600 text-transparent hover:border-slate-500"}`}
                        data-testid={`select-domain-${domain.id}`}
                        aria-label={`Select ${domain.domain}`}
                      >
                        <CheckSquare className="h-3.5 w-3.5" />
                      </button>
                      <button type="button" onClick={() => setSelectedId(domain.id)} className="min-w-0 text-left" data-testid={`inspect-domain-${domain.id}`}>
                        <span className="block break-all font-mono text-sm text-slate-100" data-testid={`domain-name-${domain.id}`}>{domain.domain}</span>
                        <span className="mt-1 flex flex-wrap items-center gap-1.5">
                          {domain.group && (
                            <span className="wx-chip" style={{ borderColor: `${domain.group.color}80`, color: domain.group.color, backgroundColor: `${domain.group.color}15` }}>
                              {domain.group.name}
                            </span>
                          )}
                          <span className={`wx-chip ${domain.assigned_users?.length ? "wx-chip-emerald" : "wx-chip-amber"}`}>
                            {domain.assigned_users?.length ? `${domain.assigned_users.length} assigned` : "Unassigned"}
                          </span>
                          {domain.tags?.includes("flagged") && (
                            <span className="wx-chip wx-chip-rose">Flagged</span>
                          )}
                        </span>
                      </button>
                      <div className="text-right">
                        <p className="font-mono text-xs text-slate-300 tabular-nums">{(domain.total_clicks || 0).toLocaleString()}</p>
                        <p className="mt-0.5 text-[10px] uppercase tracking-wide text-slate-600">clicks</p>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        <div className="min-w-0 lg:col-span-5 xl:col-span-4">
          <DomainDetailPanel
            domain={selected}
            users={users}
            groups={groups}
            editing={controls.editingId === selected?.id}
            quickAssignOpen={controls.quickAssignId === selected?.id}
            quickAssignUser={controls.quickAssignUser}
            deleteConfirmId={controls.deleteConfirmId}
            onCopy={actions.copy}
            onToggleEdit={() => actions.setEditingId(controls.editingId === selected?.id ? null : selected?.id)}
            onUpdate={(update) => actions.updateDomain(selected.id, update)}
            onToggleQuickAssign={() => actions.setQuickAssignId(controls.quickAssignId === selected?.id ? null : selected?.id)}
            onQuickAssignUser={actions.setQuickAssignUser}
            onQuickAssign={actions.quickAssign}
            onCancelQuickAssign={() => { actions.setQuickAssignId(null); actions.setQuickAssignUser(""); }}
            onRequestDelete={actions.requestDelete}
            onConfirmDelete={() => actions.removeDomain(selected.id)}
            onCancelDelete={() => actions.setDeleteConfirmId(null)}
            onUnassign={actions.requestUnassign}
          />
        </div>
      </div>
    </div>
  );
};
